// src/components/HabitTracker.js

import React, { useState, useContext, useEffect } from 'react';
import HabitRow from './HabitRow'; // Import HabitRow component
import { HabitContext } from './context/HabitContext'; // Import HabitContext
import './HabitTracker.css'; // Import CSS
import HelpModal from './HelpModal';
import { FaCheckCircle, FaCircle } from 'react-icons/fa';
import {
  FaRunning,
  FaBook,
  FaAppleAlt,
  FaDumbbell,
  FaTint,
  FaTooth,
  FaCode,
  FaMusic,
} from 'react-icons/fa';

function HabitTracker() {
  const { habits, setHabits } = useContext(HabitContext);
  const [dailyGoals, setDailyGoals] = useState([]);
  const [completedGoals, setCompletedGoals] = useState([]);
  const [newGoalText, setNewGoalText] = useState('');
  const [newHabitName, setNewHabitName] = useState('');
  const [newHabitStreak, setNewHabitStreak] = useState(0);
  const [selectedIcon, setSelectedIcon] = useState('FaRunning');
  const [showHelp, setShowHelp] = useState(false);

  // Predefined list of icons for selection
  const iconOptions = [
    { name: 'FaRunning', component: <FaRunning /> },
    { name: 'FaBook', component: <FaBook /> },
    { name: 'FaAppleAlt', component: <FaAppleAlt /> },
    { name: 'FaDumbbell', component: <FaDumbbell /> },
    { name: 'FaTint', component: <FaTint /> },
    { name: 'FaTooth', component: <FaTooth /> },
    { name: 'FaCode', component: <FaCode /> },
    { name: 'FaMusic', component: <FaMusic /> },
  ];

  // Load daily goals and completed goals from localStorage on mount
  useEffect(() => {
    const storedGoals = localStorage.getItem('dailyGoals');
    const storedCompletedGoals = localStorage.getItem('completedGoals');
    if (storedGoals) setDailyGoals(JSON.parse(storedGoals));
    if (storedCompletedGoals) setCompletedGoals(JSON.parse(storedCompletedGoals));
  }, []);

  // Save daily goals to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('dailyGoals', JSON.stringify(dailyGoals));
  }, [dailyGoals]);

  // Save completed goals to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('completedGoals', JSON.stringify(completedGoals));
  }, [completedGoals]);

  // Add new daily goal
  const addDailyGoal = () => {
    if (newGoalText.trim() === '') {
      alert('Please enter a goal.');
      return;
    }
    const newGoal = {
      id: Date.now(),
      text: newGoalText.trim(),
      completed: false,
    };
    setDailyGoals([...dailyGoals, newGoal]);
    setNewGoalText('');
  };

  // Toggle goal completion
  const toggleGoalCompletion = (id) => {
    const updatedGoals = dailyGoals.map((goal) => {
      if (goal.id === id) {
        const updatedGoal = { ...goal, completed: !goal.completed };
        if (updatedGoal.completed) {
          setCompletedGoals([...completedGoals, updatedGoal]);
          return null; // Remove from daily goals
        }
        return updatedGoal;
      }
      return goal;
    });
    setDailyGoals(updatedGoals.filter((goal) => goal !== null));
  };

  // Remove goal
  const removeGoal = (id) => {
    setDailyGoals(dailyGoals.filter((goal) => goal.id !== id));
  };

  // Remove accomplished goal
  const removeAccomplishedGoal = (id) => {
    setCompletedGoals(completedGoals.filter((goal) => goal.id !== id));
  };

  // Add new habit
  const addHabit = () => {
    if (newHabitName.trim() === '') {
      alert('Please enter a habit name.');
      return;
    }
    const startingStreak = parseInt(newHabitStreak, 10) || 0;
    const newHabit = {
      id: Date.now(),
      name: newHabitName.trim(),
      icon: selectedIcon,
      streak: startingStreak,
      completedDays: startingStreak,
      totalDays: Math.ceil(startingStreak / 7) * 7 || 7,
      lastReset: new Date().toISOString(),
    };
    setHabits([...habits, newHabit]);
    setNewHabitName('');
    setNewHabitStreak(0);
    setSelectedIcon('FaRunning'); // Reset selected icon
  };

  // Reset habits weekly
  useEffect(() => {
    const resetHabits = () => {
      const today = new Date();
      setHabits((prevHabits) =>
        prevHabits.map((habit) => {
          const lastResetDate = new Date(habit.lastReset);
          const diffInDays = Math.floor(
            (today - lastResetDate) / (1000 * 60 * 60 * 24)
          );
          if (diffInDays >= 7) {
            return {
              ...habit,
              lastReset: today.toISOString(),
            };
          }
          return habit;
        })
      );
    };

    // Check every day at midnight
    const now = new Date();
    const msUntilMidnight =
      new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1) - now;
    const timeout = setTimeout(() => {
      resetHabits();
      setInterval(resetHabits, 86400000); // Reset every 24 hours after that
    }, msUntilMidnight);

    return () => clearTimeout(timeout);
  }, [setHabits]);

  return (
    <div className="habit-tracker">
      {/* Daily Goals Section */}
      <div className="daily-goals-container">
        
        <div className="daily-goals">
        <h3>Daily Goals</h3>
          {dailyGoals.map((goal) => (
            <div
              key={goal.id}
              className={`daily-goal ${goal.completed ? 'goal-completed' : ''}`}
            >
              <div
                onClick={() => toggleGoalCompletion(goal.id)}
                className="goal-checkmark"
              >
                {goal.completed ? (
                  <FaCheckCircle color="#4caf50" />
                ) : (
                  <FaCircle />
                )}
              </div>
              <span className="goal-text">{goal.text}</span>
              <button
                onClick={() => removeGoal(goal.id)}
                className="remove-button"
              >
                X
              </button>
            </div>
          ))}
          {/* Add New Goal */}
          <div className="add-daily-goal">
  <input
    type="text"
    placeholder="New Daily Goal"
    value={newGoalText}
    onChange={(e) => setNewGoalText(e.target.value)}
  />
  <button onClick={addDailyGoal}>Add Goal</button>
  <button onClick={() => setShowHelp(true)}>
    Help
  </button>
  {showHelp && <HelpModal onClose={() => setShowHelp(false)} />}
</div>

        </div>
      </div>

      {/* Habit List */}
      <div className="habit-list-container">
        <div className="habit-list">
        <h3>Habits</h3>
          {habits.map((habit) => (
            <HabitRow
              key={habit.id}
              habit={habit}
              onToggle={(id) => {
                setHabits(
                  habits.map((h) => {
                    if (h.id === id) {
                      const updatedCompletedDays = h.completedDays + 1;
                      let newTotalDays = h.totalDays;
                      if (updatedCompletedDays > h.totalDays) {
                        newTotalDays += 7;
                      }
                      return {
                        ...h,
                        completedDays: updatedCompletedDays,
                        totalDays: newTotalDays,
                        streak: h.streak + 1,
                      };
                    }
                    return h;
                  })
                );
              }}
              onRemove={(id) => {
                setHabits(habits.filter((h) => h.id !== id));
              }}
            />
          ))}
          {/* Add New Habit */}
          <div className="add-habit">
            <input
              type="text"
              placeholder="New Habit"
              value={newHabitName}
              onChange={(e) => setNewHabitName(e.target.value)}
            />
            <input
              type="number"
              placeholder="Starting Streak"
              value={newHabitStreak}
              onChange={(e) => setNewHabitStreak(e.target.value)}
              min="0"
            />

            {/* Icon Selection */}
            <div className="icon-selection">
              <label>Select Icon:</label>
              <div className="icon-options">
                {iconOptions.map((icon) => (
                  <div
                    key={icon.name}
                    className={`icon-option ${
                      selectedIcon === icon.name ? 'selected' : ''
                    }`}
                    onClick={() => setSelectedIcon(icon.name)}
                  >
                    {icon.component}
                  </div>
                ))}
              </div>
            </div>

            <button onClick={addHabit}>Add Habit</button>
          </div>
        </div>
      </div>

      {/* Accomplishments Section */}
      <div className="accomplishments-container">
        <h3 className="section-title">Accomplishments</h3>
        <div className="accomplishments">
          {completedGoals.map((goal) => (
            <div key={goal.id} className="accomplished-goal">
              <FaCheckCircle color="#4caf50" />
              <span className="goal-text">{goal.text}</span>
              <button
                onClick={() => removeAccomplishedGoal(goal.id)}
                className="remove-button"
              >
                X
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default HabitTracker;
